import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------
export const ModuleTypeSchema = z.enum([
    'speaking',
    'writing',
    'listening',
    'reading',
]);
export const SessionKindSchema = z.enum(['warm_up', 'section', 'full_test']);
export const SessionStatusSchema = z.enum([
    'in_progress',
    'completed',
    'abandoned',
]);
// ---------------------------------------------------------------------------
// IELTS bands
// ---------------------------------------------------------------------------
// IELTS bands go from 0 to 9 in 0.5 increments. Stored as a number on the wire
// (numeric in the DB) so we don't have to deal with parsing.
export const BandScoreSchema = z
    .number()
    .min(0)
    .max(9)
    .refine((n) => Math.round(n * 2) === n * 2, {
    message: 'IELTS band must be a multiple of 0.5',
});
// Speaking criteria — IELTS Speaking band descriptors.
export const SpeakingCriteriaSchema = z.object({
    fluency_coherence: BandScoreSchema,
    lexical_resource: BandScoreSchema,
    grammatical_range_accuracy: BandScoreSchema,
    pronunciation: BandScoreSchema,
});
// Writing criteria — IELTS Writing band descriptors (same shape for Task 1 and 2).
export const WritingCriteriaSchema = z.object({
    task_achievement: BandScoreSchema,
    coherence_cohesion: BandScoreSchema,
    lexical_resource: BandScoreSchema,
    grammatical_range_accuracy: BandScoreSchema,
});
// ---------------------------------------------------------------------------
// Session feedback (per-module shape, discriminated by module)
// ---------------------------------------------------------------------------
export const SpeakingFeedbackSchema = z.object({
    module: z.literal('speaking'),
    criteria: SpeakingCriteriaSchema,
    overall_band: BandScoreSchema,
    summary: z.string(),
    strengths: z.array(z.string()).default([]),
    improvements: z.array(z.string()).default([]),
});
export const WritingFeedbackSchema = z.object({
    module: z.literal('writing'),
    criteria: WritingCriteriaSchema,
    overall_band: BandScoreSchema,
    summary: z.string(),
    annotated_response: z.string().optional(), // markdown with inline notes
});
export const ListeningFeedbackSchema = z.object({
    module: z.literal('listening'),
    raw_score: z.number().int().min(0).max(40),
    band: BandScoreSchema,
    summary: z.string().optional(),
});
export const ReadingFeedbackSchema = z.object({
    module: z.literal('reading'),
    raw_score: z.number().int().min(0).max(40),
    band: BandScoreSchema,
    summary: z.string().optional(),
});
export const SessionFeedbackSchema = z.discriminatedUnion('module', [
    SpeakingFeedbackSchema,
    WritingFeedbackSchema,
    ListeningFeedbackSchema,
    ReadingFeedbackSchema,
]);
// ---------------------------------------------------------------------------
// PracticeSession (returned to the client)
// ---------------------------------------------------------------------------
export const PracticeSessionSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    module: ModuleTypeSchema,
    kind: SessionKindSchema,
    status: SessionStatusSchema,
    startedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    overallBand: BandScoreSchema.nullable(),
    feedback: SessionFeedbackSchema.nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Attempt (returned to the client)
// ---------------------------------------------------------------------------
export const AttemptScoreSchema = z.union([
    // Speaking / writing: criteria-level
    z.object({
        type: z.literal('criteria'),
        criteria: z.record(BandScoreSchema),
        band: BandScoreSchema,
    }),
    // Listening / reading: simple correct/incorrect
    z.object({
        type: z.literal('binary'),
        correct: z.boolean(),
    }),
]);
export const AttemptSchema = z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    position: z.number().int().min(0),
    prompt: z.record(z.unknown()),
    response: z.record(z.unknown()).nullable(),
    score: AttemptScoreSchema.nullable(),
    feedback: z.string().nullable(),
    submittedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
// ---------------------------------------------------------------------------
// Request / response payloads for the sessions API
// ---------------------------------------------------------------------------
export const CreateSessionRequestSchema = z.object({
    module: ModuleTypeSchema,
    kind: SessionKindSchema.default('warm_up'),
    metadata: z.record(z.unknown()).optional(),
});
export const ListSessionsResponseSchema = z.object({
    sessions: z.array(PracticeSessionSchema),
});
